import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  Zap,
  Target,
  Users,
  Shield,
  Clock,
  TrendingUp,
  BarChart2,
  Database,
  RefreshCw,
  MessageSquare,
  ChevronRight,
} from 'lucide-react';

// ─── 10 Non-Negotiables ────────────────────────────────────────────────────────

const NON_NEGOTIABLES = [
  {
    number: '01',
    icon: Zap,
    color: 'amber',
    title: 'Speed-to-Lead Wins',
    titleAr: 'السرعة في التعامل مع العملاء هي الفائز',
    body: 'Digital leads: first contact within 5 minutes. Missed calls: callback within 15 minutes. WhatsApp: auto-acknowledgement within 2 minutes. Every minute of delay costs conversion rate.',
    bodyAr: 'العملاء الرقميون: الاتصال الأول خلال 5 دقائق. المكالمات الفائتة: معاودة الاتصال خلال 15 دقيقة. واتساب: رسالة ترحيب تلقائية خلال دقيقتين. كل دقيقة تأخير تكلف نسبة التحويل.',
    rule: '< 5 min · < 15 min · < 2 min',
    ruleAr: '< ٥ دقائق · < ١٥ دقيقة · < دقيقتان',
  },
  {
    number: '02',
    icon: Target,
    color: 'blue',
    title: 'One Pipeline. No Exceptions.',
    titleAr: 'مسار واحد. بلا استثناءات.',
    body: '11 defined stages with locked entry and exit criteria. No branch-level deviations, no custom stages. Changan, Hyundai, and Stellantis leads follow the same journey — visibility and benchmarking depend on it.',
    bodyAr: '11 مرحلة محددة بمعايير دخول وخروج ثابتة. لا انحرافات على مستوى الفرع، لا مراحل مخصصة. عملاء شانجان وهيونداي وستيلانتيس يتبعون نفس الرحلة — الرؤية والمقارنة المعيارية تعتمدان على ذلك.',
    rule: '11 stages · 3 brands · 1 definition',
    ruleAr: '١١ مرحلة · ٣ علامات تجارية · تعريف واحد',
  },
  {
    number: '03',
    icon: Users,
    color: 'violet',
    title: 'One Owner at Every Moment',
    titleAr: 'مالك واحد في كل لحظة',
    body: 'Every lead has a named owner. No shared queues, no "the team will handle it". Ownership transfers on record — with a timestamp and a reason. RACI is a live operating tool, not an org-chart decoration.',
    bodyAr: 'لكل عميل محتمل مالك باسمه. لا قوائم انتظار مشتركة، لا "الفريق سيتعامل معه". يُسجَّل نقل الملكية مع طابع زمني وسبب. RACI أداة تشغيل حية، وليست زينة هيكل تنظيمي.',
    rule: 'Named owner · Logged transfer · Timestamped',
    ruleAr: 'مالك باسمه · نقل مُسجَّل · مُختوم بالوقت',
  },
  {
    number: '04',
    icon: Database,
    color: 'emerald',
    title: 'Data Discipline is Non-Negotiable',
    titleAr: 'انضباط البيانات غير قابل للتفاوض',
    body: "The CRM is the source of truth — not memory, not WhatsApp, not Excel. Mandatory fields must be completed before stage progression. Reason codes are required on every status change. Incomplete records don't move forward.",
    bodyAr: 'نظام CRM هو مصدر الحقيقة — وليس الذاكرة، ولا واتساب، ولا إكسل. الحقول الإلزامية يجب إكمالها قبل الانتقال للمرحلة التالية. أكواد الأسباب مطلوبة عند كل تغيير في الحالة. السجلات غير المكتملة لا تتقدم.',
    rule: 'Mandatory fields · Reason codes · CRM first',
    ruleAr: 'حقول إلزامية · أكواد أسباب · CRM أولاً',
  },
  {
    number: '05',
    icon: Clock,
    color: 'rose',
    title: 'Follow-up Cadence is a System, Not a Habit',
    titleAr: 'المتابعة نظام، وليست عادة',
    body: "8 attempts over 3 days for unreachable leads — specific channels and times are prescribed, not improvised. The cadence is automated and logged. Agents don't decide when to follow up; the OS does.",
    bodyAr: '٨ محاولات على مدى ٣ أيام للعملاء الذين يتعذر الوصول إليهم — القنوات والأوقات محددة مسبقاً وليست ارتجالية. المتابعة آلية ومُسجَّلة. الوكلاء لا يقررون وقت المتابعة؛ نظام التشغيل يفعل ذلك.',
    rule: '8 attempts · 3 days · Prescribed cadence',
    ruleAr: '٨ محاولات · ٣ أيام · جدول موصوف',
  },
  {
    number: '06',
    icon: BarChart2,
    color: 'cyan',
    title: 'SLAs Are Promises, Not Targets',
    titleAr: 'اتفاقيات الخدمة وعود، وليست أهداف',
    body: "Response and progression timers are hard limits, not aspirational goals. Breaching an SLA triggers automatic escalation — to supervisor, then manager, then senior management. There is no silent breach in this OS.",
    bodyAr: 'مؤقتات الاستجابة والتقدم حدود صارمة، وليست أهدافاً طموحة. خرق اتفاقية الخدمة يُشغِّل تصعيداً تلقائياً — إلى المشرف، ثم المدير، ثم الإدارة العليا. لا يوجد خرق صامت في نظام التشغيل هذا.',
    rule: 'Hard limits · Auto-escalation · Zero silent breach',
    ruleAr: 'حدود صارمة · تصعيد تلقائي · صفر خرق صامت',
  },
  {
    number: '07',
    icon: TrendingUp,
    color: 'indigo',
    title: 'Closed-Loop from Campaign to Car',
    titleAr: 'حلقة مغلقة من الحملة إلى السيارة',
    body: 'Every lead has a source, a campaign, and a channel. Attribution must be tracked end-to-end — from media spend to appointment to sale. Marketing decisions are made on closed-loop data, not last-touch guesses.',
    bodyAr: 'كل عميل محتمل له مصدر وحملة وقناة. يجب تتبع الإسناد من البداية إلى النهاية — من الإنفاق الإعلامي إلى الموعد حتى البيع. قرارات التسويق تُتخذ على بيانات الحلقة المغلقة، لا على تخمينات آخر لمسة.',
    rule: 'Source tracked · Full attribution · Media ROI',
    ruleAr: 'المصدر مُتتبَّع · إسناد كامل · عائد الإعلانات',
  },
  {
    number: '08',
    icon: Shield,
    color: 'orange',
    title: 'Compliance is Embedded, Not Bolted On',
    titleAr: 'الامتثال مُضمَّن، وليس مُضافاً لاحقاً',
    body: "PDPL consent is captured before any outreach. WhatsApp templates are pre-approved — no off-script messaging. Opt-out requests are processed within 24 hours. Finance conversations are recorded. Compliance is a first-class citizen of this OS.",
    bodyAr: 'موافقة نظام حماية البيانات تُسجَّل قبل أي تواصل. قوالب واتساب معتمدة مسبقاً — لا رسائل خارج النص. طلبات إلغاء الاشتراك تُعالَج خلال ٢٤ ساعة. محادثات التمويل مُسجَّلة. الامتثال مواطن من الدرجة الأولى في نظام التشغيل.',
    rule: 'PDPL · Approved templates · 24h opt-out',
    ruleAr: 'نظام حماية البيانات · قوالب معتمدة · إلغاء خلال ٢٤ ساعة',
  },
  {
    number: '09',
    icon: RefreshCw,
    color: 'teal',
    title: 'No Lead Left Unresolved',
    titleAr: 'لا عميل يُترك دون حل',
    body: "Every lead exits the active pipeline with a declared outcome: Won, Lost, Nurture, Duplicate, Invalid, or Unreachable. Leads don't disappear, go cold quietly, or linger in ambiguous stages. Unresolved = escalation trigger.",
    bodyAr: 'كل عميل محتمل يغادر المسار النشط بنتيجة معلنة: فوز، أو خسارة، أو تغذية، أو مكرر، أو غير صالح، أو تعذّر الوصول. العملاء لا يختفون، ولا يبردون بصمت، ولا يتلكأون في مراحل غامضة. غير محلول = محفِّز للتصعيد.',
    rule: '6 outcomes · Mandatory closure · No cold leads',
    ruleAr: '٦ نتائج · إغلاق إلزامي · لا عملاء باردون',
  },
  {
    number: '10',
    icon: MessageSquare,
    color: 'pink',
    title: 'Managers Manage the System, Not the People',
    titleAr: 'المدراء يديرون النظام، وليس الأشخاص',
    body: "Supervisors and managers read dashboards, not inboxes. Coaching is triggered by data — stage conversion rates, cadence completion, SLA breach rates — not by intuition. The OS surfaces the signal; the manager acts on it.",
    bodyAr: 'المشرفون والمدراء يقرؤون لوحات المعلومات، وليس صناديق الوارد. التدريب يُحفَّز بالبيانات — معدلات تحويل المرحلة، اكتمال الجدول، معدلات خرق الاتفاقيات — وليس بالحدس. نظام التشغيل يُظهِر الإشارة؛ المدير يتصرف بناءً عليها.',
    rule: 'Data-driven coaching · KPI-triggered action',
    ruleAr: 'تدريب مدفوع بالبيانات · إجراء مُحفَّز بـ KPI',
  },
];

// ─── Module nav cards ──────────────────────────────────────────────────────────

const MODULES = [
  { href: '/pipeline',        label: 'Lead Pipeline',          labelAr: 'مسار العملاء',            color: 'from-blue-500 to-blue-600',     desc: '11 stages · Entry & exit criteria', descAr: '١١ مرحلة · معايير الدخول والخروج' },
  { href: '/roles',           label: 'Roles & Teams',          labelAr: 'الأدوار والفرق',           color: 'from-violet-500 to-violet-600', desc: '10 roles · RACI matrix',            descAr: '١٠ أدوار · مصفوفة المسؤوليات' },
  { href: '/sla-catalogue',   label: 'SLA Catalogue',          labelAr: 'كتالوج الاتفاقيات',        color: 'from-amber-500 to-orange-500',  desc: 'Response & escalation rules',       descAr: 'قواعد الاستجابة والتصعيد' },
  { href: '/sops',            label: 'SOPs',                   labelAr: 'إجراءات التشغيل',          color: 'from-emerald-500 to-teal-500',  desc: '16 stage & scenario procedures',    descAr: '١٦ إجراء للمراحل والسيناريوهات' },
  { href: '/operating-model', label: 'Operating Model',        labelAr: 'نموذج التشغيل',            color: 'from-cyan-500 to-blue-500',     desc: 'Org · Cadences · Governance',       descAr: 'الهيكل · الجدول · الحوكمة' },
  { href: '/implementation',  label: 'Implementation Guide',   labelAr: 'دليل التنفيذ',             color: 'from-rose-500 to-pink-500',     desc: '6-phase rollout · Tracker',         descAr: '٦ مراحل تطوير · متتبع التقدم' },
];

// ─── Color map ─────────────────────────────────────────────────────────────────

const COLOR: Record<string, { badge: string; icon: string; rule: string; num: string }> = {
  amber:   { badge: 'bg-amber-50 border-amber-200',   icon: 'bg-amber-100 text-amber-600',   rule: 'text-amber-600 bg-amber-50',   num: 'text-amber-300' },
  blue:    { badge: 'bg-blue-50 border-blue-200',     icon: 'bg-blue-100 text-blue-600',     rule: 'text-blue-600 bg-blue-50',     num: 'text-blue-200' },
  violet:  { badge: 'bg-violet-50 border-violet-200', icon: 'bg-violet-100 text-violet-600', rule: 'text-violet-600 bg-violet-50', num: 'text-violet-200' },
  emerald: { badge: 'bg-emerald-50 border-emerald-200',icon:'bg-emerald-100 text-emerald-600',rule:'text-emerald-600 bg-emerald-50',num: 'text-emerald-200' },
  rose:    { badge: 'bg-rose-50 border-rose-200',     icon: 'bg-rose-100 text-rose-600',     rule: 'text-rose-600 bg-rose-50',     num: 'text-rose-200' },
  cyan:    { badge: 'bg-cyan-50 border-cyan-200',     icon: 'bg-cyan-100 text-cyan-600',     rule: 'text-cyan-600 bg-cyan-50',     num: 'text-cyan-200' },
  indigo:  { badge: 'bg-indigo-50 border-indigo-200', icon: 'bg-indigo-100 text-indigo-600', rule: 'text-indigo-600 bg-indigo-50', num: 'text-indigo-200' },
  orange:  { badge: 'bg-orange-50 border-orange-200', icon: 'bg-orange-100 text-orange-600', rule: 'text-orange-600 bg-orange-50', num: 'text-orange-200' },
  teal:    { badge: 'bg-teal-50 border-teal-200',     icon: 'bg-teal-100 text-teal-600',     rule: 'text-teal-600 bg-teal-50',     num: 'text-teal-200' },
  pink:    { badge: 'bg-pink-50 border-pink-200',     icon: 'bg-pink-100 text-pink-600',     rule: 'text-pink-600 bg-pink-50',     num: 'text-pink-200' },
};

// ─── Component ─────────────────────────────────────────────────────────────────

const Home = () => {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <div className="space-y-20 animate-fade-in">

      {/* ══════════════════════════════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-primary-700 via-primary-800 to-slate-900 rounded-3xl overflow-hidden">
        {/* subtle grid overlay */}
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        {/* glow blobs */}
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-violet-500/15 rounded-full blur-3xl pointer-events-none" />

        <div className="relative px-8 py-16 md:py-24 lg:px-20">
          <div className="max-w-3xl">
            {/* eyebrow */}
            <p className="text-sm font-semibold tracking-widest uppercase text-blue-300 mb-5">
              {isAr ? 'المجدوعي للسيارات — المملكة العربية السعودية' : 'Almajdouie Motors · Saudi Arabia'}
            </p>

            {/* headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {isAr
                ? 'نظام تشغيل\nإدارة العملاء المحتملين'
                : <>Lead Management<br />Operating System</>}
            </h1>

            {/* sub-headline */}
            <p className="text-lg md:text-xl text-blue-100/80 max-w-2xl leading-relaxed mb-10">
              {isAr
                ? 'دليل تشغيلي شامل يحدد كيف تُكتسب الفرص، وتُتتبَّع، وتُحوَّل إلى مبيعات — عبر ثلاث علامات تجارية وجميع قنوات الاستقطاب.'
                : 'The complete operational blueprint defining how leads are captured, owned, progressed, and closed across three automotive brands and every intake channel.'}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <Link
                to="/pipeline"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-primary-800 rounded-xl font-semibold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              >
                {isAr ? 'استكشف مسار العملاء' : 'Explore the Pipeline'}
                <ArrowRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white rounded-xl font-semibold hover:bg-white/20 transition-all"
              >
                {isAr ? 'لوحة التحكم' : 'View Dashboard'}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          WHAT THIS IS — brief orientation, not stats
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="max-w-3xl mx-auto text-center space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">
          {isAr ? 'ما هذا النظام؟' : 'What This Is'}
        </h2>
        <p className="text-gray-600 leading-relaxed text-lg">
          {isAr
            ? 'نظام تشغيل إدارة العملاء المحتملين هو المرجع الموحد الذي يحكم كل خطوة في رحلة العميل — من اللحظة التي يتحول فيها الاهتمام إلى عميل محتمل، حتى تسليم السيارة. يضم معايير الأداء، وتعريفات الأدوار، وإجراءات التشغيل، وتكوين نظام CRM، وخطة التنفيذ — كلها في مكان واحد.'
            : 'The Lead Management Operating System is the single authoritative reference governing every step of the lead journey — from the moment interest becomes a lead to the moment the car is delivered. It contains performance standards, role definitions, operating procedures, CRM configuration, and the implementation plan — all in one place.'}
        </p>
        <p className="text-gray-500 text-base">
          {isAr
            ? 'صُمِّم لـ: المديرين التنفيذيين، ومديري العمليات، ومشرفي BDC، ومستشاري المبيعات، وفرق التنفيذ.'
            : 'Designed for: executives, operations managers, BDC supervisors, sales advisors, and implementation teams.'}
        </p>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          10 NON-NEGOTIABLES
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="space-y-10">
        {/* Section header */}
        <div className="text-center space-y-3">
          <p className="text-sm font-semibold tracking-widest uppercase text-primary-600">
            {isAr ? 'المبادئ الأساسية' : 'Core Principles'}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            {isAr ? 'المبادئ العشرة غير القابلة للتفاوض' : '10 Non-Negotiables'}
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            {isAr
              ? 'هذه ليست إرشادات — إنها شروط تشغيل لا تقبل التنازل. كل واحد منها يعكس قراراً تصميمياً متعمداً.'
              : "These aren't guidelines — they're operating conditions. Each one reflects a deliberate design decision that the entire system depends on."}
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid md:grid-cols-2 gap-5">
          {NON_NEGOTIABLES.map((nn) => {
            const Icon = nn.icon;
            const c = COLOR[nn.color];
            return (
              <div
                key={nn.number}
                className={`relative rounded-2xl border p-6 ${c.badge} transition-shadow hover:shadow-md`}
              >
                {/* large faded number */}
                <span className={`absolute top-4 ${isAr ? 'left-5' : 'right-5'} text-5xl font-black select-none opacity-25 ${c.num} leading-none`}>
                  {nn.number}
                </span>

                <div className="flex items-start gap-4 relative">
                  {/* icon */}
                  <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${c.icon}`}>
                    <Icon className="h-5 w-5" />
                  </div>

                  <div className="flex-1 min-w-0 space-y-2">
                    <h3 className="text-base font-bold text-gray-900 leading-snug">
                      {isAr ? nn.titleAr : nn.title}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {isAr ? nn.bodyAr : nn.body}
                    </p>
                    {/* rule pill */}
                    <p className={`inline-block text-xs font-semibold rounded-full px-3 py-1 ${c.rule}`}>
                      {isAr ? nn.ruleAr : nn.rule}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          MODULE NAVIGATION — light cards, no stats
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="space-y-6">
        <div className="text-center space-y-2">
          <h2 className="text-2xl font-bold text-gray-900">
            {isAr ? 'استكشف نظام التشغيل' : 'Explore the OS'}
          </h2>
          <p className="text-gray-500 text-sm">
            {isAr ? 'كل وحدة تُعالج بُعداً محدداً من النظام' : 'Each module addresses a specific dimension of the operating system'}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODULES.map((mod) => (
            <Link
              key={mod.href}
              to={mod.href}
              className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${mod.color} p-5 text-white hover:shadow-lg hover:-translate-y-0.5 transition-all`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-bold text-base mb-1">
                    {isAr ? mod.labelAr : mod.label}
                  </h3>
                  <p className="text-white/75 text-xs">
                    {isAr ? mod.descAr : mod.desc}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 flex-shrink-0 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 rtl:rotate-180 transition-all mt-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          SAUDI CONTEXT — compact, informative
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 px-8 py-7">
        <h2 className="text-lg font-bold text-gray-900 mb-5">
          {isAr ? 'مُصمَّم للسوق السعودي' : 'Built for the Saudi Market'}
        </h2>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
          {[
            {
              en: 'WhatsApp-first operations with pre-approved templates and PDPL-compliant opt-in handling',
              ar: 'عمليات واتساب أولاً بقوالب معتمدة مسبقاً ومعالجة اشتراك متوافقة مع نظام حماية البيانات',
            },
            {
              en: 'Evening and weekend peak coverage — staffing models aligned to Saudi buying behaviour',
              ar: 'تغطية ذروة المساء وعطلة نهاية الأسبوع — نماذج توظيف متوافقة مع سلوك الشراء السعودي',
            },
            {
              en: 'Arabic/English bilingual by default — language preference captured at first contact',
              ar: 'ثنائي اللغة العربية/الإنجليزية افتراضياً — تفضيل اللغة يُسجَّل عند أول تواصل',
            },
            {
              en: 'Finance and trade-in fast-track lanes — pre-checks and document SLAs built into every stage',
              ar: 'مسارات تمويل وسيارة مقايضة سريعة — الفحوصات المسبقة واتفاقيات وثائق مُدمَجة في كل مرحلة',
            },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center">
                <span className="text-amber-700 text-xs font-bold">{i + 1}</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                {isAr ? item.ar : item.en}
              </p>
            </div>
          ))}
        </div>
      </section>

    </div>
  );
};

export default Home;
